// The engine ↔ deck protocol (P2.1).
//
// FR-01's "two independent decks", of which one exists so far. Born at P1.1 as SP-4's measurement
// harness and promoted here once the spike had answered its question.
//
// It was kept deliberately separate from the tone generator's protocol, which shared the same
// bundle from P2.1 to P2.12: mixing them would have put a bootstrap voice's opcodes and a deck's
// in one enum, coupling two unrelated features for no reason beyond "they happen to share a file".
// **That separation is why P2.12 was a deletion rather than an untangling**, and it is worth
// keeping in mind for M3's second deck, which registers into this same module.
//
// Architecture §2's split still applies: play/pause is structural (a human pressing a button
// cannot perceive a millisecond of jitter) and rides postMessage; rate is continuous (a scratch or
// a pitch fader absolutely can perceive jitter) and rides the command ring, smoothed exactly the
// way P0.6 smooths frequency.

import type { DeckId } from './control';
import type { TransportEvent } from './transport';

/** Registered name — see audio-worklet.entry.ts. */
export const DECK_PROCESSOR = 'synapse-deck';

/**
 * One opcode today, in its own enum.
 *
 * The rule it exists to protect: **never renumber a value in place.** The two sides of the ring
 * ship in bundles that cache separately, so a stale worklet reading a reused opcode would apply a
 * gain as a rate. Add values; bump COMMAND_RING_VERSION if an existing one changes meaning. Giving
 * each processor its own table is what makes that rule easy to keep — vocabularies that cannot
 * collide by construction do not need anyone to remember.
 */
export const DECK_OPCODE = {
  /** value = playback rate, frames of track per frame of output. 1 = normal speed forward,
   * -1 = normal speed reverse, 0 = frozen, magnitude up to DECK_MAX_RATE. */
  SET_RATE: 1,
} as const;

export type DeckOpcode = (typeof DECK_OPCODE)[keyof typeof DECK_OPCODE];

/** The rate slider's range (P1.1's verify: "rate slider ±2×, including reverse"). */
export const DECK_MAX_RATE = 2;

/**
 * Normal speed, and **the value a deck is born at**.
 *
 * Exists because it was 0 from P2.5 to P2.9 and nothing ever set it, so the playhead advanced by
 * zero every sample and the deck read one frozen frame forever — silence, with a playhead that never
 * moved. Four prompts were built on top of a deck that could not make a sound, and 484 unit tests
 * had nothing to say about it: the bug lived in the seam between a host that never sent a rate and a
 * processor that defaulted to standing still.
 *
 * The old `/deck-dev` harness sent `setRate(1)` at load, which is why SP-4 could hear the deck at
 * P1.1. P2.5 replaced that harness with the real load path and did not carry the rate over; P2.7
 * deleted the harness.
 *
 * So the default is now *here*, and the processor starts at it. A deck that plays at normal speed
 * unless told otherwise is the only safe resting value — "silent until someone remembers to send a
 * command" is a footgun that has now fired once.
 */
export const DECK_DEFAULT_RATE = 1;

/**
 * The track gain a deck is born at, and the value that means "leave the track alone" (P3.2).
 *
 * Unity, for the reason `DECK_DEFAULT_RATE` exists at all: a deck whose gain waits for a message
 * before it means anything is a deck that is silent until somebody remembers to send one, and this
 * repo has already shipped that exact bug once — the processor defaulted to rate 0 and four prompts
 * were built on a deck that had never made a sound. **A resting value must be the safe one.**
 *
 * It is also FR-10's bypass. "Normalisation off" is not a mode the processor knows about; it is this
 * number, which is what keeps the audio thread free of a policy question.
 */
export const DECK_DEFAULT_TRACK_GAIN = 1;

/**
 * The largest track gain the processor will accept, linear (+12 dB).
 *
 * `loudness.ts` already caps its own boost at `LOUDNESS_MAX_GAIN_DB`, so in ordinary use this never
 * binds. It exists because the two sides of this protocol ship in bundles that cache separately, and
 * "the analysis worker is a version ahead" must not be able to put an arbitrary multiplier on the
 * audio thread. The clamp lives on the receiving side for the same reason the rate clamp does.
 */
export const DECK_MAX_TRACK_GAIN = 4;

/** Seconds for a full rate swing to complete. Shorter than the 15 ms gain ramp because a scratch
 * gesture is fast and a laggy-feeling rate response defeats the point of "artifact-free… at normal
 * rates" — but not zero, because a rate *step* (as opposed to a glide) is exactly the zipper-noise
 * class of artifact smoothing exists to prevent. */
export const DECK_RATE_RAMP_SECONDS = 0.03;

/**
 * A transport event (P2.7). Structural — postMessage, per Architecture §2.
 *
 * **The event, not the resulting state.** `{ type: 'set-playing', playing: boolean }` was here until
 * P2.7 and had to go: it puts the *decision* on the main thread, and Architecture §7 gives transport
 * to the audio thread. With cue in the picture that stops being a technicality — "cue was released"
 * means different things depending on whether the deck is previewing or was latched into play, and
 * only the side holding the state machine can tell. A UI that computed the answer would be a second
 * copy of `transport.ts` that drifts.
 *
 * So the UI reports what the human did, and the machine on the audio thread decides what it means.
 */
export interface DeckTransportMessage {
  readonly type: 'transport';
  readonly event: TransportEvent;
}

/** Jump the playhead. Structural — a seek is a discrete user action, not a stream of jitter-
 * sensitive values. */
export interface DeckSeekMessage {
  readonly type: 'seek';
  readonly frame: number;
}

/**
 * Burn `busyMs` inside every block, on purpose (P0.12's bench; inherited by the deck at P2.12).
 *
 * This exists to make the audio thread miss its deadline, which sounds like vandalism and is the
 * opposite. The underrun counter reports zero on a healthy machine — and "zero underruns" and "the
 * counter is not wired up" are the same reading. This repo has shipped that exact bug at P0.1 (an
 * inert ruleset), P0.5, P0.8 (a guard on a shadow), P0.10, P0.11 and P2.9 (a deck born at rate 0),
 * and its standing lesson (R-A) is that every gate must be watched failing once before it is
 * trusted.
 *
 * A busy-wait is the only way to cause the failure: an underrun is a real-time event, so no unit
 * test can produce one. The bench asks for it, sees the number climb, and the counter becomes a
 * measurement rather than a hope. **It moved here from the tone generator's protocol at P2.12 for
 * exactly that reason** — deleting it along with the tone generator would have left NFR-P2's
 * counter permanently unfalsifiable.
 *
 * Costs the audio thread one compare against zero per block when it is off, which is the price of
 * knowing the instrument works.
 */
export interface DeckSyntheticLoadMessage {
  readonly type: 'set-synthetic-load';
  /**
   * Milliseconds to burn per block. 0 disables it. A block is ~2.9 ms at 44.1 kHz, so 8 is a ~3x
   * overload and 2 is a thread running hot but coping.
   *
   * Milliseconds, not microseconds, because a microsecond knob here would be fiction: the audio
   * thread's only clock is `Date.now()`, which returns integer milliseconds. `performance` does not
   * exist in AudioWorkletGlobalScope — measured at P0.12, not assumed. The unit is coarse because
   * the instrument is.
   */
  readonly busyMs: number;
}

/**
 * FR-10's per-track normalisation gain, measured off the thread and applied on it (P3.2).
 *
 * **Structural, so postMessage rather than the command ring** — Architecture §2's split, applied
 * honestly rather than by reflex. The ring is for values a human streams and can hear jitter in: a
 * pitch fader, a scratch. This one arrives twice in a track's life, once when the loudness stage
 * settles and once if the user toggles the bypass, and neither is a stream. Putting it on the ring
 * would also feed Architecture §4.5's latency probe two samples per track and quietly bias a
 * distribution NFR-P1 is measured against.
 *
 * It is still *ramped* on arrival — see the processor's `#trackGain`. Structural means "not
 * jitter-sensitive in transit", not "safe to step": a gain step is a click whatever carried it.
 *
 * M4's mixer is where a continuous version arrives. FR-14's trim is a fader a hand rides, and that
 * one belongs on the ring with an opcode of its own.
 */
export interface DeckTrackGainMessage {
  readonly type: 'set-track-gain';
  /** Linear, clamped to `[0, DECK_MAX_TRACK_GAIN]` on arrival. 1 is unity and is the bypass. */
  readonly gain: number;
}

/**
 * This deck's beatgrid, for sync (P3.9).
 *
 * Structural, and sent once per track rather than streamed: a grid is a property of the file that
 * changes when a new track is loaded or FR-13's manual tools correct it, not something a hand rides.
 *
 * **The audio thread needs its own copy**, which is the whole reason this message exists. P3.9's
 * rule puts phase alignment on the audio thread — "a phase correction computed on the main thread
 * arrives late by definition" — and a correction needs both decks' grids at the instant it is
 * applied, not at the instant a button was pressed.
 *
 * `null` clears it: a track with no detected tempo has no grid, and a deck that kept the previous
 * track's would align to a beat that is not there.
 */
export interface DeckBeatGridMessage {
  readonly type: 'set-beatgrid';
  readonly grid: { readonly anchorSeconds: number; readonly beatSeconds: number } | null;
}

/**
 * Align this deck's beat phase to the other deck's (P3.9) — FR-07's second half.
 *
 * Carries **no offset**, deliberately, and that is the rule rather than an omission. The main thread
 * knows where both decks were when the button was pressed; by the time this message is handled the
 * playheads have moved, and by a different amount each if the rates differ. So the message says only
 * *do it*, and the processor computes the correction from live positions when it acts.
 *
 * It also does not act immediately: the processor waits for its own rate ramp to settle first,
 * because a phase corrected while the tempo is still gliding is a phase that drifts back out. See
 * `deck-processor.ts`'s `#pendingSync`.
 */
export interface DeckSyncPhaseMessage {
  readonly type: 'sync-phase';
}

/**
 * One hot cue action (P3.10) — FR-03.
 *
 * **`set` carries no frame**, deliberately: it means "here", and *here* is the audio thread's. The
 * transport already puts the single cue point there for the same reason — "the audio thread is what
 * *sets* it, [and] a UI copy would be a guess about a value it does not own" — and a main-thread
 * position is a block stale before it is sent.
 */
export interface DeckHotCueMessage {
  readonly type: 'hot-cue';
  readonly action: 'set' | 'clear' | 'jump';
  /** 0…`HOT_CUE_COUNT` − 1. Out of range is ignored rather than throwing; see the processor. */
  readonly slot: number;
}

/**
 * Every hot cue at once, restored from the analysis cache (P3.10).
 *
 * Separate from `hot-cue` because it is a different event: one is a user pressing a pad, the other
 * is a track arriving with cues it already had. `NaN` for an empty slot, matching `EMPTY_CUE`.
 */
export interface DeckRestoreHotCuesMessage {
  readonly type: 'restore-hot-cues';
  readonly frames: readonly number[];
}

/**
 * One loop action (P3.10) — FR-04.
 *
 * The **boundaries are computed here**, not sent: FR-04 quantizes to the beatgrid, the audio thread
 * is the side that holds the grid (P3.9 put it there for sync), and P3.10's rule puts loop
 * boundaries on the audio thread anyway because "a loop that wraps on the main thread's schedule
 * clicks". So `auto` says *four beats from here* and the processor works out where that is.
 *
 * `quantization` is the main thread's call, because it is the side that knows the tempo's
 * confidence — see `loopQuantization`. The processor is told whether to snap, never asked to judge.
 */
export interface DeckLoopMessage {
  readonly type: 'loop';
  readonly action: 'auto' | 'in' | 'out' | 'exit' | 'reloop';
  /** For `auto`: how many beats. Ignored otherwise. */
  readonly beats?: number | undefined;
  /** For `auto`: whether to snap the start to the grid, and why not when not. */
  readonly quantization?:
    'beat' | 'unquantized-no-grid' | 'unquantized-low-confidence' | undefined;
}

export type DeckMessage =
  | DeckTransportMessage
  | DeckSeekMessage
  | DeckSyntheticLoadMessage
  | DeckTrackGainMessage
  | DeckBeatGridMessage
  | DeckSyncPhaseMessage
  | DeckHotCueMessage
  | DeckRestoreHotCuesMessage
  | DeckLoopMessage;

export interface DeckProcessorOptions {
  /**
   * Which deck this processor is (FR-01: "two independent decks").
   *
   * Carried from P2.1 even though only deck A exists, and that is the whole point: M3 adds deck B,
   * and the difference between "pass 'b' to the constructor" and "retrofit identity through a
   * processor, a host, a command target and a signal source" is the difference between an
   * afternoon and a refactor. The type already existed — `DeckId` has been in
   * platform/types/control.ts since P0.2, describing ids like `deck.a.pitch` — so this is adopting
   * a vocabulary the control plane already speaks rather than inventing one.
   */
  readonly deckId: DeckId;
  readonly commandRing: SharedArrayBuffer;
  /** Created by `createTrackMemory` (platform/sab/track-memory.ts) — already fully written by
   * the time this crosses into processorOptions, per track-memory.ts's whole design. */
  readonly trackMemory: SharedArrayBuffer;
  readonly status: SharedArrayBuffer;
  /**
   * The FR-26 signal ring (P2.9). The deck writes `SIGNAL_SOURCE.DECK_A`'s block once per render
   * quantum — offsets reserved since P0.2 and unwritten until now.
   *
   * Shared with the master's publisher rather than being the deck's own ring: one frame carries
   * every source, which is what makes a deck level and a master level readable as describing the
   * same block. Two rings could not promise that.
   */
  readonly signals: SharedArrayBuffer;
  /**
   * Which ring `signals` is, as a number (P3.7).
   *
   * **A number because a `SharedArrayBuffer` cannot answer this question.** Both decks share one
   * ring and must therefore share one `SignalPublisher` on the audio thread — but `processorOptions`
   * is structured-cloned, and a cloned SAB shares its memory while being a *different JS object*.
   * Identity does not survive the journey; a number does. Minted once per ring by `AudioGraph`, and
   * `sharedSignalPublisher` has the full account of what went wrong when this was absent.
   */
  readonly signalsId: number;
  /**
   * Architecture §4.5's latency probe, laid out per `COMMAND_PROBE` (P2.12).
   *
   * **Inherited from the tone generator rather than retired with it.** The probe is §4.5
   * infrastructure, not P0.5 scaffolding, and P2.10 wired it to telemetry so NFR-P1 could be
   * measured in the field. Deleting it with its first host would have removed a required capability
   * quietly, which is the failure this repo keeps meeting from the other direction.
   *
   * **It will be sparse, and that is worth knowing rather than discovering.** The probe fills from
   * commands crossing the ring, and the deck's ring carries only `SET_RATE` — which nothing streams
   * yet, because there is no pitch fader until FR-05 lands in M3. So in ordinary use this collects
   * roughly one sample per track load. The mechanism is alive and correct; the data arrives when a
   * control that streams does.
   */
  readonly probe: SharedArrayBuffer;
}

/**
 * Playhead readback — a UI concern (so a scrub position can be drawn), not a correctness one.
 *
 * Plain reads and writes, not Atomics. `Atomics` operates on Int32Array elements; a float64
 * position has no atomic primitive to reach for without splitting it into two int32 halves for a
 * value that exists purely so a progress bar has something to show. `SignalPublisher` (P0.7)
 * already writes its own f64 timestamp field the same way — plainly — and relies on the *ring's*
 * sequence commit to make the whole slot visible; here there is no ring and no such guarantee,
 * so an occasional torn read is possible in principle. The consequence is a scrub bar glitching
 * for one frame, not corrupted audio — an acceptable trade for a position readout, and not a trade
 * this file would make for anything on the signal path itself.
 *
 * P2.9 gives the deck a real signal-ring source (`SIGNAL_SOURCE.DECK_A`, whose offsets have been
 * reserved since P0.2). Worth revisiting then whether the playhead belongs here at all or rides
 * that ring instead — one publishing mechanism is better than two.
 */
export const DECK_STATUS = {
  /** f64 — current playhead position, in fractional frames. */
  POSITION_FRAMES: 0,
  /**
   * f64 — the cue point, in frames (P2.7).
   *
   * Published rather than mirrored on the main thread, because the audio thread is what *sets* it:
   * `paused` + cue-pressed puts the cue wherever the playhead is, and the playhead is the audio
   * thread's. A UI copy would be a guess about a value it does not own.
   */
  CUE_POINT_FRAMES: 8,
  /**
   * i32 — the transport phase, as `TRANSPORT_PHASE_CODE` (P2.7).
   *
   * The UI renders this rather than tracking its own idea of play/pause. That matters most for the
   * state the UI could not compute: `previewing` ends by itself on cue release, and a button whose
   * label came from local state would show "Pause" over a stopped deck.
   *
   * An i32 so it can be read with `Atomics.load` — unlike the two f64s above, this one gates what
   * the user is *told*, and a torn read of a position is a flickering bar while a torn read of a
   * phase is a wrong label.
   */
  PHASE: 16,
  /**
   * i32 — total iterations the synthetic-load spin has burned (P2.12).
   *
   * **This field exists because the spin did not.** `#burnSyntheticLoad` was written as a `while`
   * loop with an empty body, which is correct JavaScript and, to a minifier, provably useless code:
   * esbuild compiled the whole method down to `this.#m <= 0 || Date.now() + this.#m` — the loop
   * gone, and the call site with it. So the one control that exists to prove NFR-P2's underrun
   * counter can move off zero **did nothing in every production build from P0.12 onward**, while
   * reading perfectly in source and working fine in dev, where nothing is minified.
   *
   * That is this repo's oldest failure — a guard measuring nothing — reached by a new route, and
   * the reason the fix is a counter rather than a cleverer loop.
   *
   * **The count is what the loop computes**, not merely something written next to it. Adding a
   * constant after the loop would leave the body empty and removable all over again; adding the
   * *iteration count* means the loop's result is observed, so no optimiser may drop it. And because
   * the number is readable, "the load knob did nothing" becomes something the bench asserts rather
   * than something nobody notices — a silent spin would be fixed today and quietly re-broken later.
   */
  SYNTHETIC_SPINS: 20,
  /**
   * f64 — the live phase error against the other deck, in seconds (P3.9). NaN when there is nothing
   * to compare against.
   *
   * **Published from the audio thread because AC-2's ±10 ms is a claim about the audio thread.** The
   * main thread could compute this from two playheads in one signal-ring frame — P3.7 made those
   * coherent precisely so it could — but it would be reading a frame that is already a block old and
   * comparing it against grids it holds a copy of. This is the number the thread doing the aligning
   * actually saw.
   *
   * Written every block rather than only after a sync, which is what makes it a *measurement* rather
   * than a receipt: it shows two decks drifting apart when the tempos do not quite match, which is
   * the failure mode FR-07 is judged on.
   *
   * A plain f64 like the position above it, not an atomic: a torn read is a flickering diagnostic,
   * and the phase is not what decides what the user is told (see `PHASE`).
   */
  SYNC_PHASE_ERROR_SECONDS: 24,
  /** f64 — the active loop's start, in frames. Meaningless while `LOOP_ACTIVE` is 0 (P3.10). */
  LOOP_START_FRAMES: 32,
  /** f64 — the active loop's end, in frames. */
  LOOP_END_FRAMES: 40,
  /**
   * i32, **atomic** — 1 while a loop is running, 0 otherwise (P3.10).
   *
   * Atomic for the same reason `PHASE` is: this one decides what the user is *told*. A torn position
   * is a bar that flickers; a loop button lit over a deck that is not looping is a lie the user acts
   * on.
   */
  LOOP_ACTIVE: 48,
  /**
   * f64 × `HOT_CUE_COUNT` — every hot cue's position in frames, `NaN` for an empty slot (P3.10).
   *
   * **Published rather than mirrored**, because `set` means "here" and *here* belongs to the audio
   * thread. The pads read this to know which slots are lit, and the cache reads it to know what to
   * store — both of them looking at the same numbers the transport is actually using.
   *
   * Last in the layout because it is the only variable-length run, so adding a scalar later does not
   * move eight doubles.
   */
  HOT_CUES: 64,
} as const;

/**
 * 192, up from 64 at P3.10 — the eight hot cues need 64 bytes of their own.
 *
 * Grown rather than split into a second buffer: the status buffer is rebuilt with the node on every
 * track load, and a second one would be a second thing to keep in step through `#rebuild` for no
 * benefit. 192 leaves the same kind of headroom the original 64 had, so P3.12's grid corrections do
 * not have to move anything.
 */
export const DECK_STATUS_BYTES = 192;
